import { useState, useEffect, useCallback } from "react";
import { Paper } from "@mui/material";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { getInvoicePaymentSettings, saveInvoicePaymentSettings } from "./Api";

const DAY_OPTIONS = Array.from({ length: 28 }, (_, i) => i + 1);
const MAX_DAYS = 6;

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const InvoicePaymentSettings = ({ userRole }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentDays, setPaymentDays] = useState([5, 20]);
  const [addDay, setAddDay] = useState("");

  const isCompany = (userRole || "").toLowerCase() === "company";

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvoicePaymentSettings();
      const d = res?.data || {};
      const days = Array.isArray(d.paymentDays) && d.paymentDays.length
        ? d.paymentDays
        : [d.reminderDay, d.paymentDay].filter((x) => Number.isInteger(x));
      const clean = Array.from(new Set(days.filter((x) => x >= 1 && x <= 28))).sort((a, b) => a - b);
      setPaymentDays(clean.length ? clean : [5, 20]);
    } catch { /* handled */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const sortedDays = paymentDays.slice().sort((a, b) => a - b);
  const availableToAdd = DAY_OPTIONS.filter((d) => !paymentDays.includes(d));

  const removeDay = (d) => setPaymentDays((prev) => prev.filter((x) => x !== d));
  const addNewDay = () => {
    const n = Number(addDay);
    if (!n) return;
    if (paymentDays.includes(n)) { setAddDay(""); return; }
    if (paymentDays.length >= MAX_DAYS) { toast.warning(`Up to ${MAX_DAYS} payment dates.`); return; }
    setPaymentDays((prev) => [...prev, n].sort((a, b) => a - b));
    setAddDay("");
  };

  const handleSave = async () => {
    if (!paymentDays.length) { toast.error("Add at least one payment date."); return; }
    setSaving(true);
    try {
      await saveInvoicePaymentSettings({ paymentDays: sortedDays });
      toast.success("Payment dates saved");
    } catch { /* handled */ } finally { setSaving(false); }
  };

  // "Created after the Xth → Overdue on the Yth" for each consecutive pair, the
  // last wrapping to the first date of next month.
  const overdueRules = sortedDays.map((d, i) => {
    const next = i < sortedDays.length - 1 ? sortedDays[i + 1] : sortedDays[0];
    const wraps = i === sortedDays.length - 1;
    return { from: d, to: next, wraps };
  });

  return (
    <Paper elevation={0} className="iv-wrap iv-paper">
      <div className="iv-header">
        <div className="iv-header__left">
          <div className="iv-header__icon-wrap">
            <FeatherIcon icon="calendar" size={18} color="#A5B4FC" />
          </div>
          <div className="iv-header__meta">
            <div className="iv-header__label">Finance</div>
            <div className="iv-header__title">Payment Dates</div>
          </div>
        </div>
      </div>

      <div style={{ padding: "22px 24px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "30px 0" }}><Spinner style={{ color: "#4F46E5" }} /></div>
        ) : (
          <>
            {!isCompany && (
              <div style={{ padding: "10px 14px", background: "#F8FAFC", color: "#64748B", fontSize: "0.8rem", borderRadius: 8, marginBottom: 18, border: "1px solid #E2E8F0" }}>
                These dates are managed by the company account.
              </div>
            )}

            <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "#05966915", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FeatherIcon icon="dollar-sign" size={15} color="#059669" />
              </div>
              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#0F172A" }}>Monthly Payment Dates</div>
            </div>
            <div style={{ fontSize: "0.74rem", color: "#64748B", marginBottom: 12 }}>
              The days each month invoices are paid. An invoice becomes <strong>Overdue</strong> on the next payment date after it was created if it hasn't been paid. Reminders to accounting are sent on these same dates.
            </div>

            {/* Selected date chips */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
              {sortedDays.length === 0 && (
                <span style={{ color: "#94A3B8", fontSize: "0.82rem" }}>No payment dates yet — add one below.</span>
              )}
              {sortedDays.map((d) => (
                <span key={d} style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "#EEF2FF", border: "1px solid #C7D2FE", color: "#3730A3",
                  borderRadius: 20, padding: "6px 12px", fontSize: "0.84rem", fontWeight: 700,
                }}>
                  <FeatherIcon icon="calendar" size={13} />
                  {ordinal(d)} of the month
                  {isCompany && (
                    <button
                      type="button"
                      onClick={() => removeDay(d)}
                      title="Remove"
                      disabled={sortedDays.length <= 1}
                      style={{
                        border: "none", background: "transparent", padding: 0, marginLeft: 2,
                        cursor: sortedDays.length <= 1 ? "not-allowed" : "pointer",
                        color: sortedDays.length <= 1 ? "#C7D2FE" : "#6366F1", display: "flex",
                      }}
                    >
                      <FeatherIcon icon="x" size={14} />
                    </button>
                  )}
                </span>
              ))}
            </div>

            {/* Add a date */}
            {isCompany && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
                <select
                  value={addDay}
                  onChange={(e) => setAddDay(e.target.value)}
                  disabled={availableToAdd.length === 0 || paymentDays.length >= MAX_DAYS}
                  style={{
                    border: "1px solid #E2E8F0", borderRadius: 8, padding: "9px 12px",
                    fontSize: "0.86rem", fontWeight: 600, color: "#0F172A", background: "#fff",
                    minWidth: 180, outline: "none",
                  }}
                >
                  <option value="">Select a day…</option>
                  {availableToAdd.map((d) => (
                    <option key={d} value={d}>{ordinal(d)} of the month</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="iv-btn"
                  style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", color: "#4F46E5" }}
                  onClick={addNewDay}
                  disabled={!addDay || paymentDays.length >= MAX_DAYS}
                >
                  <FeatherIcon icon="plus" size={14} /> Add date
                </button>
                {paymentDays.length >= MAX_DAYS && (
                  <span style={{ fontSize: "0.74rem", color: "#94A3B8" }}>Maximum of {MAX_DAYS} dates.</span>
                )}
              </div>
            )}

            {/* Overdue rule preview */}
            <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10, padding: "12px 16px", fontSize: "0.8rem", color: "#1E40AF" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 700, marginBottom: 6 }}>
                <FeatherIcon icon="info" size={15} /> How overdue works
              </div>
              {overdueRules.length === 0 ? (
                <span>Add a payment date to see the schedule.</span>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                  {overdueRules.map((r, i) => (
                    <li key={i}>
                      Invoices created after the <strong>{ordinal(r.from)}</strong> are marked <strong>Overdue</strong> on the{" "}
                      <strong>{ordinal(r.to)}</strong>{r.wraps ? " of next month" : ""} if unpaid.
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {isCompany && (
              <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end" }}>
                <button type="button" className="iv-btn iv-btn--primary" onClick={handleSave} disabled={saving}>
                  {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={14} />}
                  Save Settings
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Paper>
  );
};

export { InvoicePaymentSettings };
